import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, Phone, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { api, gbp } from "../lib/api";
import { CONTACT, imgV } from "../lib/content";
import { useShop } from "../context/ShopContext";
import { FadeUp } from "../components/Reveal";
import ProductCarousel from "../components/ProductCarousel";
import RecentlyViewed from "../components/RecentlyViewed";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState("");
  const [metal, setMetal] = useState("");
  const [chain, setChain] = useState("");
  const [width, setWidth] = useState("");
  const [sizeConfirmed, setSizeConfirmed] = useState(false);
  const [shopSettings, setShopSettings] = useState(null);
  const [denomination, setDenomination] = useState("");
  const [recipient, setRecipient] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [deliveryMode, setDeliveryMode] = useState("post");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const { addToCart, toggleWishlist, wishlist, trackView } = useShop();

  useEffect(() => {
    api.get("/settings").then(({ data }) => setShopSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setProduct(null);
    setSize("");
    setDenomination("");
    setRecipient("");
    setGiftMessage("");
    setNotFound(false);
    setActiveImg(0);
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      trackView(data);
      document.title = `${data.name} — Celtic Gold`;
      const el = document.querySelector('meta[name="description"]');
      if (el) el.setAttribute("content", (data.description || `${data.name} at Celtic Gold, Peel, Isle of Man.`).slice(0, 158));
      api.get(`/products/${slug}/related`).then(({ data: r }) => setRelated(r.items)).catch(() => {});
    }).catch(() => setNotFound(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-48 pb-32 text-center" data-testid="product-not-found">
        <h1 className="font-serif text-4xl text-ivory mb-4">Piece not found</h1>
        <p className="text-stone-mist mb-8">It may have been sold or removed from the online catalogue.</p>
        <Link to="/shop/all" className="text-gold text-xs uppercase tracking-[0.25em] hover:text-gold-glow">Back to the shop</Link>
      </main>
    );
  }

  if (!product) {
    return <main className="pt-48 pb-32 max-w-7xl mx-auto px-6"><div className="aspect-square bg-ink-3 animate-pulse max-w-lg" /></main>;
  }

  const images = product.images?.length ? product.images : product.image ? [product.image] : [];
  const wished = wishlist.some((w) => w.id === product.id);
  const out = product.stock_status === "out_of_stock";
  const isVoucher = product.product_type === "voucher";
  const denominations = isVoucher ? (product.options || []) : [];

  // variation model (existing prices only — never modified)
  const variants = product.variants || [];
  const metals = variants.length
    ? [...new Set(variants.map((v) => v.metal).filter(Boolean))]
    : [product.metal, ...(product.metals || [])].filter(Boolean);
  const stockMetals = shopSettings?.stock_metals || ["Silver", "9ct Yellow Gold"];
  const selMetal = metal || metals.find((m) => (product.stock_overrides || {})[m] === "in_stock") || metals.find((m) => stockMetals.includes(m)) || metals[0] || "";
  const isRing = product.product_type === "ring";
  const hasWidths = variants.some((v) => v.width);
  const widthsForMetal = hasWidths
    ? [...new Set(variants.filter((v) => v.metal === selMetal && v.width).map((v) => v.width))].sort(
        (a, b) => parseFloat(a) - parseFloat(b)
      )
    : [];
  const selWidth = hasWidths ? width : null;
  const sizesForMetal = hasWidths
    ? product.sizes || []
    : variants.length
    ? [...new Set(variants.filter((v) => v.metal === selMetal && v.size).map((v) => v.size))]
    : product.sizes || [];
  const hasChain = product.product_type === "pendant" && variants.some((v) => v.chain && v.chain !== "none");
  const needsSize = isRing && sizesForMetal.length > 0;
  const needsChain = hasChain;
  const selChain = hasChain ? chain : null;

  const resolveSelection = () => {
    if (!variants.length) return { price: product.price, poa: false };
    if (hasWidths) {
      let cand = variants;
      if (selMetal) { const m = cand.filter((v) => v.metal === selMetal); if (m.length) cand = m; }
      if (selWidth) { const w = cand.filter((v) => v.width === selWidth); if (w.length) cand = w; }
      const exact = size ? cand.filter((v) => v.size === size && v.price != null) : [];
      if (exact.length) return { price: Math.min(...exact.map((v) => v.price)), poa: false };
      if (cand.some((v) => v.poa)) return { price: null, poa: true };
      const priced = cand.filter((v) => v.price != null);
      return { price: priced.length ? Math.min(...priced.map((v) => v.price)) : product.price, poa: false };
    }
    let cand = variants;
    if (selMetal) { const m = cand.filter((v) => v.metal === selMetal); if (m.length) cand = m; }
    if (size) { const s = cand.filter((v) => v.size === size); if (s.length) cand = s; }
    if (selChain) { const c = cand.filter((v) => v.chain === selChain); if (c.length) cand = c; }
    return { price: cand.length ? Math.min(...cand.map((v) => v.price)) : product.price, poa: false };
  };
  const sel = resolveSelection();
  const displayPrice = isVoucher && denomination ? parseFloat(denomination.slice(1)) : (sel.price ?? product.price);
  const priceExact = isVoucher ? !!denomination : (!variants.length || (!!selMetal && (!needsSize || !!size) && (!needsChain || !!selChain) && (!hasWidths || !!selWidth)));

  const overrides = product.stock_overrides || {};
  const availability = selMetal
    ? overrides[selMetal] || (stockMetals.includes(selMetal) ? "in_stock" : "made_to_order")
    : null;
  const madeToOrder = availability === "made_to_order";
  const leadTime = shopSettings?.lead_time || "approximately 6 weeks";

  const voucherReady =
    !!denomination && (deliveryMode === "post" || (!!recipient.trim() && /.+@.+\..+/.test(recipientEmail)));
  const variationReady = (metals.length ? !!selMetal : true) && (!hasWidths || !!selWidth) && (!needsSize || !!size) && (!needsChain || !!selChain);
  const isPoa = !isVoucher && variationReady && sel.poa;
  const canAdd = !out && !isPoa && (isVoucher ? voucherReady : (variationReady && (!needsSize || sizeConfirmed)));
  const personalisation = isVoucher
    ? [recipient ? `Recipient: ${recipient}` : "", giftMessage ? `Message: ${giftMessage}` : ""].filter(Boolean).join(" — ")
    : "";

  return (
    <main className="pt-36 lg:pt-44 pb-24" data-testid="product-detail">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs text-stone-mist mb-10 font-mono">
          <Link to="/shop/all" className="hover:text-gold">Shop</Link>
          {product.categories?.[0] && (
            <> / <Link to={`/shop/${product.categories[0]}`} className="hover:text-gold capitalize">{product.categories[0].replace("-", " ")}</Link></>
          )}
          {" "}/ <span className="text-gold">{product.name}</span>
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <FadeUp>
            <div className="border border-white/5 bg-white overflow-hidden">
              {images[activeImg] ? (
                <img
                  key={images[activeImg]}
                  src={imgV(images[activeImg])}
                  alt={product.name}
                  className="w-full aspect-square object-contain animate-[fadeIn_0.3s_ease-out]"
                  data-testid="product-image"
                />
              ) : (
                <div className="w-full aspect-square flex items-center justify-center text-stone-mist text-xs font-mono">Image coming soon</div>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-3 mt-3" data-testid="product-thumbnails">
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveImg(i)}
                    aria-label={`View photo ${i + 1} of ${product.name}`}
                    data-testid={`product-thumbnail-${i}`}
                    className={`aspect-square overflow-hidden border transition-[border-color,opacity] duration-200 focus:outline-none ${
                      i === activeImg ? "border-gold" : "border-white/5 opacity-60 hover:opacity-100 hover:border-gold/40"
                    }`}
                  >
                    <img src={imgV(img)} alt="" className="w-full h-full object-contain bg-white" />
                  </button>
                ))}
              </div>
            )}
          </FadeUp>

          <FadeUp delay={0.1}>
            {product.collections?.length > 0 && (
              <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">
                {product.collections.map((c) => c.replace(/-/g, " ")).join(" · ")}
              </p>
            )}
            <h1 className="font-serif text-4xl sm:text-5xl text-ivory font-light tracking-tight leading-[1.05]" data-testid="product-name">{product.name}</h1>
            <p className="font-mono text-2xl text-gold mt-6" data-testid="product-price">
              {isPoa ? (
                <span className="text-base uppercase tracking-[0.2em]" data-testid="price-on-request">Price on Request</span>
              ) : (
                <>
                  {!priceExact && <span className="text-xs uppercase tracking-[0.2em] text-stone-mist mr-2">from</span>}
                  {gbp(displayPrice)}
                </>
              )}
            </p>

            <div className="flex items-center gap-3 mt-4">
              <span className={`w-2 h-2 rounded-full ${out ? "bg-red-400" : madeToOrder ? "bg-gold" : "bg-emerald-400"}`} />
              <span className="text-sm text-stone-mist" data-testid="product-availability">
                {out ? "Sold out — enquire for similar pieces" : madeToOrder ? `Made to Order — ${leadTime}` : "✓ In Stock"}
              </span>
            </div>

            <p className="text-stone-mist font-light leading-relaxed mt-8 whitespace-pre-line">{product.description}</p>

            <div className="border-y border-white/10 py-5 mt-8 space-y-2 text-sm">
              {(product.metals?.length > 0 || product.metal) && (
                <p className="flex justify-between gap-4">
                  <span className="text-stone-mist shrink-0">Metal</span>
                  <span className="text-ivory text-right">{product.metals?.length ? product.metals.join(" · ") : product.metal}</span>
                </p>
              )}
              {product.product_type && (
                <p className="flex justify-between"><span className="text-stone-mist">Type</span><span className="text-ivory capitalize">{product.product_type}</span></p>
              )}
              {product.options?.length > 0 && (
                <p className="flex justify-between gap-4">
                  <span className="text-stone-mist shrink-0">Options</span>
                  <span className="text-ivory text-right">{product.options.join(" · ")}</span>
                </p>
              )}
              {product.sku && (
                <p className="flex justify-between"><span className="text-stone-mist">Reference</span><span className="font-mono text-ivory text-xs pt-0.5">{product.sku}</span></p>
              )}
            </div>

            {isVoucher && (
              <div className="mt-8 border border-gold/25 bg-gold/5 p-6 space-y-5" data-testid="voucher-options">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">Choose a value</p>
                  <div className="flex flex-wrap gap-2" data-testid="denomination-options">
                    {denominations.map((d) => (
                      <button
                        key={d}
                        onClick={() => setDenomination(denomination === d ? "" : d)}
                        data-testid={`denomination-${d.replace("£", "")}`}
                        className={`px-4 h-11 border text-sm font-mono transition-colors ${
                          denomination === d ? "border-gold bg-gold text-ink font-medium" : "border-white/15 text-ivory hover:border-gold/60"
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">How should we deliver it?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ["post", "Post the voucher"],
                      ["email", "Email to recipient"],
                    ].map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setDeliveryMode(v)}
                        data-testid={`voucher-delivery-${v}`}
                        className={`border px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors ${
                          deliveryMode === v ? "border-gold bg-gold text-ink font-medium" : "border-white/15 text-ivory hover:border-gold/60"
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                  {deliveryMode === "email" && (
                    <p className="text-[11px] text-stone-mist mt-2">The voucher is emailed to the recipient once your payment is confirmed.</p>
                  )}
                </div>
                <input
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder={deliveryMode === "email" ? "Recipient name *" : "Recipient name (optional)"}
                  data-testid="voucher-recipient"
                  className="w-full bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory placeholder:text-stone-mist/50 transition-colors"
                />
                {deliveryMode === "email" && (
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="Recipient email *"
                    data-testid="voucher-recipient-email"
                    className="w-full bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory placeholder:text-stone-mist/50 transition-colors"
                  />
                )}
                <textarea
                  value={giftMessage}
                  onChange={(e) => setGiftMessage(e.target.value)}
                  placeholder="Personal message (optional)"
                  rows={3}
                  data-testid="voucher-message"
                  className="w-full bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory placeholder:text-stone-mist/50 transition-colors"
                />
              </div>
            )}

            {!isVoucher && (metals.length > 0 || sizesForMetal.length > 0 || hasChain) && (
              <div className="mt-8 space-y-6" data-testid="variation-selectors">
                {metals.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Metal</p>
                    <select
                      value={selMetal}
                      onChange={(e) => { setMetal(e.target.value); setWidth(""); setSize(""); }}
                      data-testid="metal-select"
                      className="w-full sm:w-72 bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory transition-colors"
                    >
                      {metals.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <p className={`text-xs mt-2 ${madeToOrder ? "text-gold" : "text-emerald-400"}`} data-testid="metal-availability">
                      {madeToOrder ? `Made to Order — ${leadTime}` : "✓ In Stock"}
                    </p>
                  </div>
                )}
                {hasWidths && widthsForMetal.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Width</p>
                    <select
                      value={width}
                      onChange={(e) => { setWidth(e.target.value); setSize(""); }}
                      data-testid="width-select"
                      className="w-full sm:w-72 bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory transition-colors"
                    >
                      <option value="">Select Width</option>
                      {widthsForMetal.map((w) => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                )}
                {sizesForMetal.length > 0 && ["ring", "chain", "bangle", "bracelet"].includes(product.product_type) && !isRing && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Size / Length</p>
                    <select
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      data-testid="size-select"
                      className="w-full sm:w-72 bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory transition-colors"
                    >
                      <option value="">Select</option>
                      {sizesForMetal.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                {sizesForMetal.length > 0 && isRing && (
                  <div className="border border-white/10 bg-ink-2/50 px-5 py-5" data-testid="ring-size-block">
                    <p className="text-xs uppercase tracking-[0.25em] text-gold mb-2">Choose Your Ring Size</p>
                    <p className="text-stone-mist text-xs leading-relaxed mb-4">
                      Please select your ring size below before adding your ring to the basket.
                    </p>
                    <label className="block text-xs uppercase tracking-[0.2em] text-stone-mist mb-2">UK Ring Size:</label>
                    <select
                      value={size}
                      onChange={(e) => { setSize(e.target.value); setSizeConfirmed(false); }}
                      data-testid="size-select"
                      className="w-full sm:w-72 bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory transition-colors"
                    >
                      <option value="">Select your size</option>
                      {sizesForMetal.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <p className="text-stone-mist text-xs mt-2" data-testid="available-sizes">
                      Available sizes: {sizesForMetal.join(", ")}
                    </p>
                    <p className="text-stone-mist text-xs leading-relaxed mt-4">
                      <span className="text-ivory">Not sure of your ring size?</span>{" "}
                      Please use our{" "}
                      <a href="/terms#ring-sizing" className="text-gold hover:text-gold-glow transition-colors underline underline-offset-2">
                        ring size guide
                      </a>{" "}
                      to measure your finger accurately before ordering. If you are between sizes, we recommend checking with our team before placing your order.
                    </p>
                    <label className="flex items-start gap-3 mt-4 cursor-pointer" data-testid="size-confirm-label">
                      <input
                        type="checkbox"
                        checked={sizeConfirmed}
                        onChange={(e) => setSizeConfirmed(e.target.checked)}
                        data-testid="size-confirm-checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#C5A059]"
                      />
                      <span className="text-xs text-ivory leading-relaxed">
                        <span className="uppercase tracking-[0.15em] text-stone-mist block">Ring Size:</span>
                        I have selected my correct ring size
                      </span>
                    </label>
                    <p className="text-stone-mist text-[11px] leading-relaxed mt-3">
                      Please note: Rings made in a specific size cannot always be resized, particularly personalised or made-to-order pieces. Please ensure your size is correct before completing your purchase.
                    </p>
                  </div>
                )}
                {hasChain && (() => {
                  const chainVals = [...new Set(variants.filter((v) => v.metal === selMetal && v.chain).map((v) => v.chain))];
                  const nonePrices = variants.filter((v) => v.metal === selMetal && v.chain === "none").map((v) => v.price);
                  const baseMin = nonePrices.length ? Math.min(...nonePrices) : null;
                  const order = ["none", "standard", "superior", "included"];
                  const opts = chainVals.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                  const label = (cv) => {
                    if (cv === "none") return "Pendant Only";
                    if (cv === "included") return "Pendant + Chain";
                    const name = cv === "standard" ? 'Standard 18" Chain' : cv === "superior" ? 'Superior 18" Chain' : cv;
                    const rows = variants.filter((v) => v.metal === selMetal && v.chain === cv).map((v) => v.price);
                    const delta = rows.length && baseMin != null ? Math.min(...rows) - baseMin : null;
                    return delta != null ? `${name} (+£${delta.toFixed(2)})` : name;
                  };
                  return (
                    <div>
                      <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Chain</p>
                      <select
                        value={chain}
                        onChange={(e) => setChain(e.target.value)}
                        data-testid="chain-select"
                        className="w-full sm:w-72 bg-ink-2 border border-white/10 focus:border-gold outline-none px-4 py-3.5 text-sm text-ivory transition-colors"
                      >
                        <option value="">Select an option</option>
                        {opts.map((cv) => <option key={cv} value={cv}>{label(cv)}</option>)}
                      </select>
                    </div>
                  );
                })()}
                {madeToOrder && (
                  <div className="border border-gold/30 bg-gold/5 px-5 py-4" data-testid="mto-notice">
                    <p className="text-gold text-xs uppercase tracking-[0.2em] mb-1">Made to Order</p>
                    <p className="text-stone-mist text-xs leading-relaxed">
                      Your selected option is made especially for you and has an estimated production time of {leadTime}.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-10">
              {isPoa ? (
                <Link
                  to={`/contact?subject=${encodeURIComponent(`Price request: ${product.name}${selMetal ? ` — ${selMetal}` : ""}${selWidth ? `, ${selWidth}` : ""}${size ? `, Size ${size}` : ""}`)}`}
                  data-testid="request-price"
                  className="flex-1 bg-gold text-ink py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors duration-300 text-center"
                >
                  Request a Price
                </Link>
              ) : (
                <button
                  onClick={() =>
                    addToCart(product, isVoucher ? denomination : size || null, 1, personalisation, {
                      delivery_mode: isVoucher ? deliveryMode : null,
                      recipient_email: isVoucher && deliveryMode === "email" ? recipientEmail : null,
                      metal: isVoucher ? null : selMetal || null,
                      chain: isVoucher ? null : selChain,
                      width: isVoucher ? null : selWidth,
                      unit_price: displayPrice,
                      availability,
                      lead_time: leadTime,
                    })
                  }
                  disabled={!canAdd}
                  data-testid="add-to-basket"
                  className="flex-1 bg-gold text-ink py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Add to Basket
                </button>
              )}
              <button
                onClick={() => toggleWishlist(product)}
                data-testid="wishlist-add"
                aria-label="Add to wishlist"
                className={`w-14 border flex items-center justify-center transition-colors duration-300 ${
                  wished ? "border-gold bg-gold text-ink" : "border-white/15 text-ivory hover:border-gold hover:text-gold"
                }`}
              >
                <Heart className="w-5 h-5" fill={wished ? "currentColor" : "none"} />
              </button>
            </div>

            <Link
              to={`/contact?subject=${encodeURIComponent(`Product enquiry: ${product.name}`)}`}
              data-testid="product-enquire"
              className="block text-center border border-gold/40 text-gold py-4 text-xs uppercase tracking-[0.25em] mt-3 hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              Ask a Question About This Piece
            </Link>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 text-xs text-stone-mist">
              <Link to="/shipping" className="flex items-start gap-3 border border-white/5 p-4 hover:border-gold/40 transition-colors" data-testid="pdp-delivery-link">
                <Truck className="w-4 h-4 text-gold shrink-0 mt-0.5" /> Delivery from £3.75 — see options
              </Link>
              <Link to="/terms#returns" className="flex items-start gap-3 border border-white/5 p-4 hover:border-gold/40 transition-colors" data-testid="pdp-returns-link">
                <RotateCcw className="w-4 h-4 text-gold shrink-0 mt-0.5" /> Exchanges offered — see policy
              </Link>
              <a href={`tel:${CONTACT.phoneShort.replace(/\s/g, "")}`} className="flex items-start gap-3 border border-white/5 p-4 hover:border-gold/40 transition-colors" data-testid="pdp-call">
                <Phone className="w-4 h-4 text-gold shrink-0 mt-0.5" /> Need help? Call {CONTACT.phoneShort}
              </a>
            </div>
          </FadeUp>
        </div>

        {related.length > 0 && (
          <section className="mt-24 lg:mt-32" data-testid="related-products">
            <div className="flex items-center gap-4 mb-10">
              <ShieldCheck className="w-4 h-4 text-gold" />
              <h2 className="font-serif text-3xl text-ivory font-light">You May Also Like</h2>
            </div>
            <ProductCarousel products={related} testid="related-carousel" />
          </section>
        )}

        <RecentlyViewed excludeId={product.id} />
      </div>
    </main>
  );
}
